import axios from 'axios';
import { nanoid } from 'nanoid';
import { Api } from '@api/Api';
import { API_BASE_URL, RECAPTCHA_V3_SITE_KEY } from '@config/constants';
import { waitForPage } from '@helpers/waitForPage';

export const apiClient = new Api({
  baseURL: API_BASE_URL
});

let tokenGenerator = undefined as
  | ((ignoreCache?: boolean) => Promise<string>)
  | undefined
  | null;

export function setTokenGenerator(
  generator: ((ignoreCache?: boolean) => Promise<string>) | null
) {
  tokenGenerator = generator;
}

let logoutHandler = undefined as (() => void) | undefined | null;

export function setLogoutHandler(handler: (() => void) | null) {
  logoutHandler = handler;
}

let networkErrorHandler = undefined as
  | ((isNetworkError: boolean) => void)
  | undefined
  | null;

export function setNetworkErrorHandler(
  handler: (isNetworkError: boolean) => void | null
) {
  networkErrorHandler = handler;
}

apiClient.instance.interceptors.response.use(undefined, (err) => {
  if (axios.isCancel(err)) throw err;
  if (!axios.isAxiosError(err)) throw err;
  if (!err.config) throw err;
  const requestMethod = err.config.method?.toUpperCase();
  const requestUrl = err.config.url;
  err.message = `[${requestMethod}] ${requestUrl}: ${err.message}`;
  throw err;
});

apiClient.instance.interceptors.response.use(undefined, (err) => {
  const { code, config, response } = err;

  if (axios.isCancel(err)) {
    return Promise.reject(err);
  }

  // Failed before being sent, by a request interceptor: getting the access
  // token, which retries itself. Not logged here, once per waiting request.
  // Auth0's 'timeout' after those retries: its login iframe not answering,
  // like a network error
  if (!config) {
    if (err?.error === 'timeout' && networkErrorHandler) {
      networkErrorHandler(true);
    }
    return Promise.reject(err);
  }

  const isTimeout = code === 'ECONNABORTED';
  const isNetworkError = !response || response.status === 0;
  const isSafeServerError =
    response?.status === 408 ||
    response?.status === 429 ||
    (response?.status >= 500 && response?.status <= 599);
  const isIdempotentRequest = [
    'get',
    'head',
    'options',
    'delete',
    'put'
  ].includes(config.method.toLowerCase());
  const isAuthError = response?.status === 401;
  // Figma's limit for the user's Figma account: each try counts against it,
  // and Figma's wait (`retryAfter`) can be hours.
  const isFigmaRateLimit = response?.data?.errorCode === 'figma_rate_limited';
  const retryCount: number = isAuthError
    ? 1
    : isFigmaRateLimit
      ? 0
      : (config.retry ?? 0);
  const attempt: number = config.attempt ?? 0;
  const attemptsLeft = Math.max(0, retryCount - attempt);

  const needRetry =
    attemptsLeft > 0 &&
    (isAuthError ||
      (isIdempotentRequest &&
        (isTimeout || isNetworkError || isSafeServerError)));

  if (!needRetry) {
    if (isNetworkError || isTimeout) {
      console.error('Network error, all retries exhausted.');
      if (networkErrorHandler) networkErrorHandler(true);
    }
    if (isAuthError) {
      console.warn('Logout due to auth error.');
      if (logoutHandler) logoutHandler();
    }
    return Promise.reject(err);
  }

  const minWaitSeconds = 0.5;
  const maxWaitSeconds = 10;
  const exponentialRate = 2;
  const waitSeconds =
    Math.min(maxWaitSeconds, minWaitSeconds * exponentialRate ** attempt) +
    Math.random();
  console.log(
    `Retrying ${config.method} request ${config.url} in ${waitSeconds}s`
  );
  config.attempt = attempt + 1;
  // Paused offline, where it would fail and use up an attempt. Not in hidden
  // tabs: a save made there should still go through.
  const delayRetryRequest = new Promise<void>((resolve) => {
    setTimeout(() => resolve(), waitSeconds * 1000);
  }).then(() => waitForPage(['online'], config.signal));
  config.ignoreAccessTokenCache =
    !!config.ignoreAccessTokenCache || isAuthError;
  return delayRetryRequest.then(() => apiClient.instance(config));
});

apiClient.instance.interceptors.request.use(async (config: any) => {
  if (!config.headers.get('authorization')) {
    const ignoreAccessTokenCache = !!config.ignoreAccessTokenCache;
    const token = tokenGenerator
      ? await tokenGenerator(ignoreAccessTokenCache)
      : undefined;
    if (token) {
      config.headers.set('authorization', `Bearer ${token}`);
    }
  }
  config.retry = config.retry ?? 6;
  config.attempt = config.attempt ?? 0;
  return config;
});

export const last100CorrelationIds: string[] = [];

apiClient.instance.interceptors.request.use(async (config) => {
  const actionMethods = ['post', 'put', 'patch', 'delete'];
  if (!config.method || !actionMethods.includes(config.method.toLowerCase())) {
    return config;
  }
  const correlationId = nanoid();
  if (last100CorrelationIds.length > 100) {
    last100CorrelationIds.shift();
  }
  last100CorrelationIds.push(correlationId);
  config.headers.set('x-correlation-id', correlationId);
  return config;
});

apiClient.instance.interceptors.request.use(async (config) => {
  const actionMethods = ['post', 'put', 'patch', 'delete'];
  const method = config.method?.toLowerCase() ?? '';
  if (!actionMethods.includes(method)) return config;
  const path = config.url ?? '';
  const actionName = `${method}_${path}`.replaceAll(/[^A-Za-z/_]/g, '_');
  if ('grecaptcha' in window) {
    await new Promise<void>((resolve) => {
      (window as any).grecaptcha.ready(resolve);
    });
    const token = await (window as any).grecaptcha.execute(
      RECAPTCHA_V3_SITE_KEY,
      { action: actionName }
    );
    config.headers.set('x-captcha', token);
  }
  return config;
});

apiClient.instance.interceptors.request.use(async (config) => {
  if (config.url !== '/profile/onboarding/step/complete') {
    config.headers.set('x-demo', window.location.hostname.startsWith('demo.'));
  }
  return config;
});
